import type { Fraction } from '@mimir/common/util/fractionType';
import { calculateRawAudioFrameBufferSize, calculateRawVideoFrameBufferSize, calculateSampleCountForFrameNumber, calculateSampleOffsetForFrameNumber } from '@mimir/common/transcoding/rawFrame';
import type { FrameContext, FrameProviderV2, Listener, FrameV2, FrameInfoV2, VideoInfoV2, AudioInfoV2, VideoFrameV2, AudioFrameV2, PreloadInfo } from './frameProvider';
import type { Resolution } from '@mimir/common/transcoding/types';
import type { PaintBuffer} from '@mimir/common/pixelManipulation/paintContext';
import { BLACK, createPaintContext, YELLOW } from '@mimir/common/pixelManipulation/paintContext';
import { drawText } from '@mimir/common/pixelManipulation/drawText';
import { divideFractions, formatFractionAsExpression } from '@mimir/common/util/integerMath';
import type { BufferCache } from './refCountedBuffer';

export function createDummyFrameProvider(resolution: Resolution, frameRate: Fraction, sampleRate: number, bufferCache: BufferCache): FrameProviderV2 {
  return createDummyFrameProviderInternal(resolution, frameRate, sampleRate, bufferCache);
}

// ------

function createDummyFrameProviderInternal(resolution: Resolution, frameRate: Fraction, sampleRate: number, bufferCache: BufferCache): FrameProviderV2 {
  const audioInfo : AudioInfoV2 = {
    sampleRate: 48000,
    channelCount: 2,
    format: 'float32',
  };
  const videoInfo : VideoInfoV2 = {
    resolution,
    pixelFormat: 'rgba',
    fieldOrder: 'progressive',
  };

  const rawInfo : FrameInfoV2 = {
    frameRate,
    video: videoInfo,
    audio: audioInfo,
  };

  const squareSize = 30.0;
  const halfSquareSize = squareSize / 2.0;
  const bounceSpace = { width: (resolution.width / 2) - squareSize, height: (resolution.height / 2) - squareSize };

  let closed = false;

  return {
    setListener(listener: Listener): void {
      // no-op
    },

    async getInfo(_signal: AbortSignal): Promise<FrameInfoV2> {
      return rawInfo;
    },
    async getFrame(frameNumber: number, _preloadHint: PreloadInfo | null, _signal: AbortSignal): Promise<FrameV2> {
      if (closed) {
        throw new Error('getFrame on a closed FrameProvider.');
      }
      const videoBufferSize = calculateRawVideoFrameBufferSize(resolution.width, resolution.height, videoInfo.pixelFormat);

      const squareRepeatX = frameNumber % (bounceSpace.width * 2);
      const squareRepeatY = frameNumber % (bounceSpace.height * 2);
      const squareX = ((squareRepeatX >= bounceSpace.width) ? (bounceSpace.width * 2) - squareRepeatX : squareRepeatX) + halfSquareSize;
      const squareY = ((squareRepeatY >= bounceSpace.height) ? (bounceSpace.height * 2) - squareRepeatY : squareRepeatY) + halfSquareSize;

      const beep =
        (squareRepeatX === 0) ||
        (squareRepeatY === 0) ||
        (squareRepeatX === bounceSpace.width) ||
        (squareRepeatY === bounceSpace.height);

      const videoBuffer = bufferCache.allocateBuffer(
        videoBufferSize,
        (videoBuffer) => {
          const paintBuffer : PaintBuffer = {
            width: resolution.width,
            height: resolution.height,
            pixelFormat: videoInfo.pixelFormat,
            pixelData: videoBuffer,
          };

          const paintContext = createPaintContext(paintBuffer);

          for (let i = -halfSquareSize; i < halfSquareSize; i++) {
            for (let j = -halfSquareSize; j < halfSquareSize; j++) {
              paintContext.setPixelRgb(squareX + i, squareY + j, { r: 255, g: 0, b: 255 });
            }
          }

          const exactPos = divideFractions({ numerator: frameNumber, denominator: 1 }, frameRate);

          drawText(paintContext, 0, 0, YELLOW, BLACK, `pos=${formatFractionAsExpression(exactPos)}`);

          let tmp = frameNumber;
          for (let i = 0; i < 16; i++) {
            const v = ((tmp % 2) === 0) ? 128 : 64;
            // eslint-disable-next-line no-bitwise
            tmp = (tmp >> 1);
            paintContext.setPixelRgb(15 - i, 0, { r: v, g: v, b: v });
          }
        });

      const videoFrame : VideoFrameV2 = { ...videoInfo, buffer: videoBuffer };

      const sampleOffset = calculateSampleOffsetForFrameNumber(sampleRate, frameRate, frameNumber);
      const sampleCount = calculateSampleCountForFrameNumber(sampleRate, frameRate, frameNumber);
      const audioBufferSize = calculateRawAudioFrameBufferSize(audioInfo, sampleCount);

      const audioBuffer =
        bufferCache.allocateBuffer(
          audioBufferSize,
          (buffer: Uint8Array) => {
            const samples = new Float32Array(buffer.buffer);

            const notes = [0, 2, 4, 5, 7, 9, 11];
            const beepFrequency = 440 * Math.pow(Math.pow(2, (1 / 12.0)), notes[frameNumber % 7]);
            for (let i = 0; i < sampleCount; i++) {
              const time = (sampleOffset + i) / audioInfo.sampleRate;
              const sample = Math.sin(time * 220.0 * Math.PI * 2.0) * 0.1;
              const beepSample = Math.sin(time * beepFrequency * Math.PI * 2.0) * 0.2;
              const beepVolume = beep ? (1.0 - Math.cos((i / sampleCount) * Math.PI * 2.0)) / 2.0 : 0.0;
              for (let j = 0; j < audioInfo.channelCount; j++) {
                samples[(i * audioInfo.channelCount) + j] = sample + (beepSample * beepVolume);
              }
            }

          }
        );

      const audioFrame : AudioFrameV2 = {
        ...audioInfo,
        sampleCount,
        sampleOffset,
        buffer: audioBuffer,
      };

      return {
        frameRate,
        frameNumber,
        video: videoFrame,
        audio: audioFrame,
      };
    },
    async getFrameByContext(_context: FrameContext, _signal: AbortSignal): Promise<FrameV2 | null> {
      return null;
    },
    async preload(
      _frameStart: number,
      _preloadInfo: PreloadInfo,
      _signal: AbortSignal
    ): Promise<void> {
      // TODO: Maybe it would be interesting to simulate some form of buffering cost.
    },
    close(): void {
      closed = true;
    }
  };
}
